<template>
  <div
    class="product-gallery-item"
    @mousedown="startDrag"
    @mousemove="onDrag"
    @mouseup="endDrag"
    @mouseleave="endDrag"
    @touchstart="startDrag"
    @touchmove="onDrag"
    @touchend="endDrag"
    @click="handleClick"
  >
    <div class="zoom-wrapper" :class="{ zoomed: localIsZoomed }">
      <lazy-image
        class="product-gallery-item-image"
        ref="picture"
        :item="picture"
        :sizes="sizes"
        rootMargin="0px"
        loading="lazy"
        render-when-appeared="true"
        draggable="false"
        :style="imageStyle"
      />
    </div>
  </div>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'
import ImageManager from 'vue-storefront-prismic-ssr/utilities/ImageManager'
import LazyImage from './utils/LazyImage'

export default {
  props: ['item', 'isZoomed'],
  name: 'ProductZoomGalleryItem',
  mixins: [ReadyHook],
  components: {
    LazyImage
  },
  data() {
    return {
      localIsZoomed: this.isZoomed,
      isDragging: false,
      isClick: true,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 }
    }
  },
  computed: {
    picture() {
      return {
        url: this.item.src,
        lowDefinitionUrl: ImageManager.getLowDefinition(
          this.item.src,
          150,
          1,
          true
        ),
        srcset: ImageManager.getSrcSet(this.item.src, 3200, 600, 100, 1, true),
        alt: '',
        width: this.item.width,
        height: this.item.height
      }
    },
    sizes() {
      return '100vw'
    },
    imageStyle() {
      return {
        transform: `translate(${this.dragOffset.x}px, ${this.dragOffset.y}px)`
      }
    }
  },
  methods: {
    toggleZoom() {
      this.localIsZoomed = !this.localIsZoomed
      this.$emit('zoom-changed', this.localIsZoomed)
      if (!this.localIsZoomed) {
        this.dragOffset = { x: 0, y: 0 } // Reset offset
      }
    },
    handleClick() {
      if (this.isClick) {
        this.toggleZoom()
      }
      this.isClick = true // Reset click state
    },
    startDrag(event) {
      if (!this.localIsZoomed) return

      const { x, y } = this.getEventCoordinates(event)
      this.isDragging = true
      this.isClick = true
      this.dragStart = { x, y }
    },
    onDrag(event) {
      if (!this.isDragging) return

      const { x, y } = this.getEventCoordinates(event)
      this.isClick = false

      const dx = x - this.dragStart.x
      const dy = y - this.dragStart.y

      this.dragOffset.x += dx
      this.dragOffset.y += dy

      this.dragStart = { x, y } // Update start point
    },
    endDrag() {
      this.isDragging = false
    },
    getEventCoordinates(event) {
      if (event.touches && event.touches.length > 0) {
        return {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        }
      }
      return { x: event.clientX, y: event.clientY }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.product-gallery-item {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .zoom-wrapper {
    width: 100%;
    height: 100%;
    transition: transform 0.2s ease-in-out;
    cursor: zoom-in;

    &.zoomed {
      transform: scale(2.5);
      cursor: zoom-out;
      @include bp(sm) {
        transform: scale(2);
      }
    }
  }

  &-image {
    width: 100%;
    height: 100%;

    /deep/ img {
      height: auto;
      width: 100%;
      max-height: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      object-fit: cover;

      @include bp(sm) {
        height: 100%;
        width: auto;
        display: block;
        margin: 0 auto;
      }
    }
  }
}
</style>
